import { ref } from 'vue'
import { useChatStore } from '@/stores/useChat'
import type { MessageData } from '@/components/Bubble/types'

interface UseStreamingChatOptions {
  api?: string
  clientId?: string
  username?: string
}

export function useStreamingChat(options: UseStreamingChatOptions = {}) {
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const chatStore = useChatStore()

  // AbortController to handle stream interruptions
  let currentAbortController: AbortController | null = null

  // Function to save partial conversation to server
  async function savePartialConversation(
    messages: Array<{ role: string, content: string }>,
    partialResponse: string,
  ): Promise<void> {
    if (!partialResponse.trim() || !options.api || !options.clientId) {
      return
    }

    try {
      // Save the conversation including the partial response
      const conversationMessages = [
        ...messages,
        { role: 'assistant', content: partialResponse }
      ]

      // Use regular chat endpoint to save the conversation
      const saveUrl = options.api.replace('/stream', '')

      await fetch(saveUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-client-id': options.clientId,
          'x-username': options.username || 'unknown',
        },
        body: JSON.stringify({ messages: conversationMessages }),
      })

      console.log('Partial conversation saved successfully')
    } catch (saveError) {
      console.warn('Failed to save partial conversation:', saveError)
    }
  }

  // Function to abort current streaming
  function abortStreaming(): void {
    if (currentAbortController) {
      currentAbortController.abort()
      currentAbortController = null
    }
  }

  async function sendStreamingMessage(
    messages: Array<{ role: string, content: string }>,
  ): Promise<string> {
    if (isLoading.value) {
      throw new Error('Already processing a message')
    }

    isLoading.value = true
    error.value = null

    // Create new AbortController for this request
    currentAbortController = new AbortController()
    const abortSignal = currentAbortController.signal

    // Add loading message to store
    const loadingMessageIndex = chatStore.responses.length
    chatStore.updateResponses({
      isBeforeMessage: true,
      isAfterMessage: true,
      isLoadingMessage: true,
      time: new Date(),
    })

    let fullResponse = ''

    try {
      // Use the streaming endpoint from the backend
      const streamUrl = options.api?.replace('/chat', '/chat/stream') || '/api/chat/stream'

      const response = await fetch(streamUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-client-id': options.clientId || 'random-client-id',
          'x-username': options.username || 'unknown',
        },
        body: JSON.stringify({ messages }),
        signal: abortSignal, // Add abort signal
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('No reader available')
      }

      const decoder = new TextDecoder()

      // Add streaming message to store
      let streamingMessageAdded = false

      try {
        while (true) {
          // Check if aborted
          if (abortSignal.aborted) {
            throw new Error('Stream was aborted')
          }

          const { done, value } = await reader.read()
          if (done)
            break

          const chunk = decoder.decode(value)
          const lines = chunk.split('\n')

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6)
              if (data.trim() === '')
                continue

              try {
                const parsed = JSON.parse(data)

                if (parsed.type === 'content') {
                  fullResponse += parsed.content

                  if (!streamingMessageAdded) {
                    // Replace loading message with streaming message
                    chatStore.responses[loadingMessageIndex] = {
                      messageContent: fullResponse,
                      isBeforeMessage: true,
                      isAfterMessage: true,
                      time: new Date(),
                      isStreamingMessage: true,
                    } as MessageData
                    streamingMessageAdded = true
                  }
                  else {
                    // Update immediately for fast streaming
                    chatStore.responses[loadingMessageIndex] = {
                      ...chatStore.responses[loadingMessageIndex],
                      messageContent: fullResponse,
                    }
                  }
                }
                else if (parsed.type === 'function_start') {
                  // Function call started - you could add UI feedback here
                  // eslint-disable-next-line no-console
                  console.log('Function call started:', parsed.function_name)
                }
                else if (parsed.type === 'complete') {
                  // Stream complete
                  fullResponse = parsed.total_response
                  break
                }
                else if (parsed.type === 'error') {
                  throw new Error(parsed.message)
                }
              }
              catch (parseError) {
                console.warn('Failed to parse SSE data:', data, parseError)
              }
            }
          }
        }
      }
      finally {
        reader.releaseLock()
      }

      // Finalize the message
      if (streamingMessageAdded) {
        chatStore.responses[loadingMessageIndex] = {
          ...chatStore.responses[loadingMessageIndex],
          messageContent: fullResponse,
          isStreamingMessage: false,
        }
      }
      else {
        // Fallback - replace loading message with final response
        chatStore.deleteLastResponse(fullResponse)
      }

      return fullResponse
    }
    catch (err) {
      error.value = err instanceof Error ? err.message : 'An error occurred'

      // Check if this was an abort and we have partial content to save
      if (err instanceof Error && err.message === 'Stream was aborted' && fullResponse.trim()) {
        console.log('Stream was interrupted, saving partial response:', fullResponse.length, 'characters')

        // Keep the partial message in the UI
        if (chatStore.responses[loadingMessageIndex]) {
          chatStore.responses[loadingMessageIndex] = {
            ...chatStore.responses[loadingMessageIndex],
            messageContent: fullResponse,
            isStreamingMessage: false,
            isLoadingMessage: false,
          }
        }

        // Save partial conversation to server
        await savePartialConversation(messages, fullResponse)

        // Return the partial response instead of throwing
        return fullResponse
      }

      // Remove loading message on other errors
      if (chatStore.responses[loadingMessageIndex]?.isLoadingMessage) {
        chatStore.responses.splice(loadingMessageIndex, 1)
      }

      throw err
    }
    finally {
      isLoading.value = false
      currentAbortController = null
    }
  }

  return {
    sendStreamingMessage,
    abortStreaming,
    isLoading,
    error,
  }
}

// Extended MessageData type to support streaming
declare module '@/components/Bubble/types' {
  interface MessageData {
    isStreamingMessage?: boolean
  }
}
